import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, Monitor, Smartphone, RefreshCw, Loader2 } from "lucide-react";
import { apiRequest, getQueryFn } from "@/lib/queryClient";
import LpExpressRenderer from "@/components/lp-express-renderer";
import type { Brand, Product, SalesDiscount, ProductReview } from "@shared/schema";
import { expandBundleProducts } from "@shared/lp-express-bundle";
import LpExpressSavedStudioPreview from "@/components/lp-express-saved-studio-preview";

type PreviewResponse = {
  generatedContent: Record<string, unknown>;
  meta: { personaId: string; angleId: string; saleId: string | null };
};

export default function LpExpressPreviewPage() {
  const pageId = new URLSearchParams(window.location.search).get("pageId");
  return pageId ? <LpExpressSavedStudioPreview pageId={pageId} /> : <ProductPreviewPage />;
}

function ProductPreviewPage() {
  const { brandId } = useParams<{ brandId: string }>();

  const [viewMode, setViewMode] = useState<"desktop" | "mobile">("desktop");
  const containerWidth = viewMode === "desktop" ? "100%" : "390px";

  // Brand-video fetch state. Declared ABOVE the brand query because the query's
  // refetchInterval closure reads it (reading state declared below the query
  // would be a temporal-dead-zone ReferenceError).
  const [videosFetchActive, setVideosFetchActive] = useState(false);
  const videoFetchStarted = useRef(false);

  const { data: brand, isLoading: loadingBrand } = useQuery<Brand>({
    queryKey: ["/api/brands", brandId],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
    // While a brand-video fetch is in flight, poll the brand so the newly
    // mirrored video URLs surface in the preview as soon as they're saved.
    refetchInterval: videosFetchActive ? 5000 : false,
  });
  const { data: products = [], isLoading: loadingProducts } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });
  const { data: sales = [] } = useQuery<SalesDiscount[]>({
    queryKey: [`/api/brands/${brandId}/sales-discounts`],
    enabled: !!brandId,
  });
  const { data: reviewsByProduct = {} } = useQuery<Record<string, ProductReview[]>>({
    queryKey: [`/api/brands/${brandId}/product-reviews`],
    enabled: !!brandId,
  });

  const product = products[0] ?? null;
  const sale = sales.find((s) => s.status === "active") ?? null;
  // Expand a bundle product into its member SKUs for the renderer's
  // multi-product "Included in kit" UI; non-bundles stay a single-item list.
  const renderProducts = expandBundleProducts(product, products);

  const [generatedContent, setGeneratedContent] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previewMutation = useMutation<PreviewResponse, Error, void>({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/brands/${brandId}/lp-express/preview/${product!.id}`,
        {},
      );
      return (await res.json()) as PreviewResponse;
    },
    onSuccess: (data) => {
      setGeneratedContent(data.generatedContent);
      setError(null);
    },
    onError: (e) => setError(e.message || "Failed to load preview"),
  });

  useEffect(() => {
    if (!product || previewMutation.isPending || generatedContent !== null) return;
    previewMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // ── Brand-video fetch ──────────────────────────────────────────────────────
  // When the brand has no saved Farmstand video for one or both slots, kick off
  // a one-time background fetch (scrape the brand's Instagram → mirror to R2 →
  // save the URLs) and poll the brand until they fill in. The renderer shows a
  // loading spinner (not the dashed TODO placeholder) for missing slots while
  // videosFetchActive is true.
  const videoMakingUrl = (brand as any)?.videoMakingUrl ?? null;
  const videoLifestyleUrl = (brand as any)?.videoLifestyleUrl ?? null;

  useEffect(() => {
    if (!brand || videoFetchStarted.current) return;
    if (videoMakingUrl && videoLifestyleUrl) return; // both slots already filled
    videoFetchStarted.current = true;
    setVideosFetchActive(true);
    apiRequest("POST", `/api/brands/${brandId}/lp-express/fetch-videos`, {}).catch(() => {
      // Service is best-effort; a failure just means we stop showing the spinner.
      setVideosFetchActive(false);
    });
  }, [brand, brandId, videoMakingUrl, videoLifestyleUrl]);

  // Stop polling/spinner once both slots are filled.
  useEffect(() => {
    if (videosFetchActive && videoMakingUrl && videoLifestyleUrl) {
      setVideosFetchActive(false);
    }
  }, [videosFetchActive, videoMakingUrl, videoLifestyleUrl]);

  // Safety timeout — the scrape can run for several minutes; never spin forever.
  useEffect(() => {
    if (!videosFetchActive) return;
    const t = setTimeout(() => setVideosFetchActive(false), 6 * 60 * 1000);
    return () => clearTimeout(t);
  }, [videosFetchActive]);

  const isLoading = loadingBrand || loadingProducts;

  if (isLoading) {
    return (
      <div className="p-8 max-w-5xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-[400px] rounded-xl" />
      </div>
    );
  }

  const pageTitle = `LP Express — ${product?.name ?? "No product"}`;

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      {/* Header — matches Landing Page Builder shell */}
      <div className="flex items-center gap-3">
        <Link href={`/brands/${brandId}/landing-pages`}>
          <Button variant="ghost" size="icon" className="shrink-0 rounded-full" data-testid="button-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold tracking-tight truncate" data-testid="text-page-title">{pageTitle}</h1>
          <p className="text-xs text-muted-foreground">Landing Page Builder</p>
        </div>
        <Badge className="text-[10px] uppercase tracking-wider">DRAFT</Badge>
      </div>

      <Tabs defaultValue="preview" className="w-full">
        <TabsList className="w-full justify-start" data-testid="tabs-lp-express">
          <TabsTrigger value="offer" disabled>Offer Setup</TabsTrigger>
          <TabsTrigger value="sections" disabled>Sections</TabsTrigger>
          <TabsTrigger value="ai-draft" disabled>AI Draft</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="preview" className="mt-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm text-muted-foreground">
                  Live preview of your LP Express layout and copy.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  disabled={!product || previewMutation.isPending}
                  onClick={() => previewMutation.mutate()}
                  data-testid="button-regenerate"
                >
                  {previewMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  <span className="ml-1">Regenerate</span>
                </Button>
              </div>
              <div className="flex items-center gap-1 border border-border/40 rounded-lg p-0.5">
                <Button
                  variant={viewMode === "desktop" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2.5"
                  onClick={() => setViewMode("desktop")}
                  data-testid="button-preview-desktop"
                >
                  <Monitor className="h-3.5 w-3.5 mr-1.5" />
                  Desktop
                </Button>
                <Button
                  variant={viewMode === "mobile" ? "secondary" : "ghost"}
                  size="sm"
                  className="h-7 px-2.5"
                  onClick={() => setViewMode("mobile")}
                  data-testid="button-preview-mobile"
                >
                  <Smartphone className="h-3.5 w-3.5 mr-1.5" />
                  Mobile
                </Button>
              </div>
            </div>

            <div
              className="mx-auto border border-border/40 rounded-xl overflow-hidden shadow-sm bg-white"
              style={{ width: containerWidth, maxWidth: "100%", maxHeight: "70vh", overflowY: "auto" }}
              data-testid="preview-container"
            >
              {!product ? (
                <div className="p-12 text-center text-stone-600">
                  No products in this brand yet. Add one to preview the renderer.
                </div>
              ) : previewMutation.isPending && !generatedContent ? (
                <div className="p-12 text-center text-stone-600 flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Running Sim (hero + lead-in)…
                </div>
              ) : error && !generatedContent ? (
                <div className="p-12 text-center text-red-600 space-y-3">
                  <div>Preview failed: {error}</div>
                  <Button
                    variant="outline"
                    onClick={() => previewMutation.mutate()}
                    data-testid="button-retry-preview"
                  >
                    Retry
                  </Button>
                </div>
              ) : (
                <LpExpressRenderer
                  brand={brand ?? null}
                  rootProduct={product}
                  products={renderProducts}
                  sale={sale}
                  generatedContent={generatedContent ?? {}}
                  reviewsByProduct={reviewsByProduct}
                  videosLoading={videosFetchActive}
                />
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
